import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { colors } from '../core/theme/theme';

interface Props {
  open: boolean;
  onClose: () => void;
  onSignOut: () => void;
  onChangeLanguage: (lang: 'en' | 'fr') => void;
  currentLanguage: 'en' | 'fr';
  isAdmin: boolean;
}

export default function MoreDrawer({
  open,
  onClose,
  onSignOut,
  onChangeLanguage,
  currentLanguage,
  isAdmin,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const go = (path: string) => () => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 0,
            maxHeight: '75vh',
            paddingBottom: 'env(safe-area-inset-bottom)',
          },
        },
      }}
    >
      <List disablePadding>
        <ListItemButton onClick={go('/kronos/timesheet')} sx={{ minHeight: 56 }}>
          <ListItemIcon><ViewWeekOutlinedIcon /></ListItemIcon>
          <ListItemText primary={t('nav.timesheet')} />
        </ListItemButton>
        <ListItemButton onClick={go('/kronos/calendar')} sx={{ minHeight: 56 }}>
          <ListItemIcon><CalendarMonthIcon /></ListItemIcon>
          <ListItemText primary={t('nav.calendar')} />
        </ListItemButton>
        <ListItemButton onClick={go('/kronos/dashboard')} sx={{ minHeight: 56 }}>
          <ListItemIcon><DashboardOutlinedIcon /></ListItemIcon>
          <ListItemText primary={t('nav.dashboard')} />
        </ListItemButton>
        <ListItemButton onClick={go('/kronos/report')} sx={{ minHeight: 56 }}>
          <ListItemIcon><AssessmentOutlinedIcon /></ListItemIcon>
          <ListItemText primary={t('nav.report')} />
        </ListItemButton>
        <ListItemButton onClick={go('/customers')} sx={{ minHeight: 56 }}>
          <ListItemIcon><BusinessOutlinedIcon /></ListItemIcon>
          <ListItemText primary={t('nav.customers')} />
        </ListItemButton>
        {isAdmin && (
          <ListItemButton onClick={go('/settings')} sx={{ minHeight: 56 }}>
            <ListItemIcon><SettingsOutlinedIcon /></ListItemIcon>
            <ListItemText primary={t('nav.settings')} />
          </ListItemButton>
        )}

        <Divider />

        <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1.5 }}>
          <Button
            variant={currentLanguage === 'en' ? 'contained' : 'outlined'}
            onClick={() => onChangeLanguage('en')}
            sx={{ flex: 1, bgcolor: currentLanguage === 'en' ? colors.yellow : undefined, color: '#000' }}
          >
            EN
          </Button>
          <Button
            variant={currentLanguage === 'fr' ? 'contained' : 'outlined'}
            onClick={() => onChangeLanguage('fr')}
            sx={{ flex: 1, bgcolor: currentLanguage === 'fr' ? colors.yellow : undefined, color: '#000' }}
          >
            FR
          </Button>
        </Box>

        <Divider />

        <ListItemButton onClick={onSignOut} sx={{ minHeight: 56, color: colors.red }}>
          <ListItemIcon sx={{ color: colors.red }}><LogoutOutlinedIcon /></ListItemIcon>
          <ListItemText primary={t('nav.signOut')} />
        </ListItemButton>
      </List>
    </Drawer>
  );
}
